import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, CheckCircle2, X, Users } from "lucide-react";

const emptyJob = {
  title: "", description: "", company_name: "", location: "", country: "US",
  job_type: "part-time", industry: "retail", min_age: 16, max_age: 21,
  pay_min: null, pay_max: null, pay_period: "hour",
  remote: false, experience_required: false, work_permit_required: true,
  school_friendly: true, tags: [], apply_url: null,
};

export default function EmployerDashboard() {
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyJob);

  const load = async () => {
    const [j, a] = await Promise.all([api.get("/employer/jobs"), api.get("/employer/applications")]);
    setJobs(j.data.jobs || []);
    setApps(a.data.applications || []);
  };
  useEffect(()=>{ load(); }, []);

  const openNew = () => { setEditing("new"); setForm(emptyJob); };
  const openEdit = (job) => { setEditing(job.id); setForm({ ...emptyJob, ...job }); };
  const cancel = () => setEditing(null);

  const save = async () => {
    try {
      const payload = { ...form,
        min_age: Number(form.min_age), max_age: Number(form.max_age),
        pay_min: form.pay_min ? Number(form.pay_min) : null,
        pay_max: form.pay_max ? Number(form.pay_max) : null,
        tags: typeof form.tags === "string" ? form.tags.split(",").map(t=>t.trim()).filter(Boolean) : (form.tags||[]),
      };
      if (editing === "new") await api.post("/jobs", payload);
      else await api.put(`/jobs/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this job?")) return;
    try { await api.delete(`/jobs/${id}`); toast("Deleted"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const markFilled = async (id) => {
    try { await api.post(`/jobs/${id}/fill`); toast.success("Marked filled"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const setAppStatus = async (id, status_val) => {
    try { await api.put(`/employer/applications/${id}/status`, null, { params: { status_val } }); toast.success(`Status: ${status_val}`); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  return (
    <div className="nh-container py-10">
      <Toaster position="top-center"/>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-4xl">Employer Dashboard</h1>
          <p className="text-[var(--nh-muted)] mt-1">Post jobs, review applicants, and support youth talent — free.</p>
        </div>
        <button className="nh-btn nh-btn-primary" onClick={openNew} data-testid="emp-new-job"><Plus size={14}/> Post New Job</button>
      </div>

      <div className="flex gap-2 mt-6">
        <button onClick={()=>setTab("jobs")} className={`nh-btn text-xs ${tab==="jobs" ? "nh-btn-secondary" : "nh-btn-ghost"}`} data-testid="tab-jobs">My Jobs ({jobs.length})</button>
        <button onClick={()=>setTab("apps")} className={`nh-btn text-xs ${tab==="apps" ? "nh-btn-secondary" : "nh-btn-ghost"}`} data-testid="tab-apps">Applicants ({apps.length})</button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="nh-card bg-white w-full max-w-3xl max-h-[90vh] overflow-auto p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-black text-2xl">{editing==="new" ? "New Job Post" : "Edit Job"}</h2>
              <button onClick={cancel} className="nh-btn nh-btn-ghost text-xs"><X size={14}/></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <input placeholder="Title" className="nh-input md:col-span-2" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="jf-title"/>
              <input placeholder="Company name" className="nh-input" value={form.company_name} onChange={(e)=>setForm({...form, company_name:e.target.value})} data-testid="jf-company"/>
              <input placeholder="Location" className="nh-input" value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} data-testid="jf-location"/>
              <select className="nh-input" value={form.job_type} onChange={(e)=>setForm({...form, job_type:e.target.value})} data-testid="jf-type">
                {["part-time","full-time","internship","seasonal","volunteer","apprenticeship","gig"].map(t=><option key={t}>{t}</option>)}
              </select>
              <select className="nh-input" value={form.industry} onChange={(e)=>setForm({...form, industry:e.target.value})} data-testid="jf-industry">
                {["retail","food service","tech","nonprofit","healthcare","sports","entertainment","government","trades","education","customer service"].map(t=><option key={t}>{t}</option>)}
              </select>
              <div>
                <label className="text-xs font-bold uppercase">Min Age</label>
                <input type="number" min="13" max="21" className="nh-input" value={form.min_age} onChange={(e)=>setForm({...form, min_age:e.target.value})} data-testid="jf-min-age"/>
              </div>
              <div>
                <label className="text-xs font-bold uppercase">Max Age</label>
                <input type="number" min="13" max="65" className="nh-input" value={form.max_age} onChange={(e)=>setForm({...form, max_age:e.target.value})} data-testid="jf-max-age"/>
              </div>
              <input placeholder="Pay min" type="number" className="nh-input" value={form.pay_min||""} onChange={(e)=>setForm({...form, pay_min:e.target.value})}/>
              <input placeholder="Pay max" type="number" className="nh-input" value={form.pay_max||""} onChange={(e)=>setForm({...form, pay_max:e.target.value})}/>
              <select className="nh-input" value={form.pay_period||"hour"} onChange={(e)=>setForm({...form, pay_period:e.target.value})}>
                {["hour","day","week","month","year"].map(t=><option key={t}>{t}</option>)}
              </select>
              <input placeholder="Apply URL (optional)" className="nh-input" value={form.apply_url||""} onChange={(e)=>setForm({...form, apply_url:e.target.value})}/>
              <input placeholder="Tags (comma-separated)" className="nh-input md:col-span-2" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e)=>setForm({...form, tags:e.target.value})}/>
              <textarea rows={5} placeholder="Description" className="nh-input md:col-span-2" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="jf-description"/>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.remote} onChange={(e)=>setForm({...form, remote:e.target.checked})}/> Remote</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.school_friendly} onChange={(e)=>setForm({...form, school_friendly:e.target.checked})}/> School-friendly</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.work_permit_required} onChange={(e)=>setForm({...form, work_permit_required:e.target.checked})}/> Work permit required</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.experience_required} onChange={(e)=>setForm({...form, experience_required:e.target.checked})}/> Experience required</label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={cancel} className="nh-btn nh-btn-ghost text-xs">Cancel</button>
              <button onClick={save} className="nh-btn nh-btn-primary text-xs" data-testid="jf-save">Save Job</button>
            </div>
          </div>
        </div>
      )}

      {tab === "jobs" ? (
        <div className="mt-6 grid md:grid-cols-2 gap-5">
          {jobs.length === 0 ? <p className="text-sm text-[var(--nh-muted)]">No jobs yet.</p> : jobs.map(j=>(
            <div key={j.id} className="nh-card p-5" data-testid={`emp-job-${j.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display font-black text-lg">{j.title}</h3>
                  <p className="text-sm text-[var(--nh-muted)]">{j.company_name} · {j.location}</p>
                </div>
                <span className="nh-tag bg-[var(--nh-mint)]">{j.status}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="nh-tag bg-white">{j.job_type}</span>
                <span className="nh-tag bg-white">Ages {j.min_age}+</span>
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={()=>openEdit(j)} className="nh-btn nh-btn-ghost text-xs">Edit</button>
                {j.status !== "filled" && <button onClick={()=>markFilled(j.id)} className="nh-btn nh-btn-secondary text-xs"><CheckCircle2 size={14}/> Mark filled</button>}
                <button onClick={()=>remove(j.id)} className="nh-btn nh-btn-ghost text-xs text-red-600"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-6 nh-card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black text-white text-left"><tr><th className="p-3">Applicant</th><th className="p-3">Age</th><th className="p-3">Job</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
            <tbody>
              {apps.length === 0 ? <tr><td className="p-4 text-[var(--nh-muted)]" colSpan={5}>No applications yet.</td></tr> : apps.map(a=>(
                <tr key={a.id} className="border-t-2 border-black" data-testid={`emp-app-${a.id}`}>
                  <td className="p-3 font-bold">{a.youth?.name}<br/><span className="text-xs text-[var(--nh-muted)]">{a.youth?.email}</span></td>
                  <td className="p-3">{a.youth?.age || "—"}</td>
                  <td className="p-3">{a.job?.title}</td>
                  <td className="p-3"><span className="nh-tag bg-[var(--nh-mint)]">{a.status}</span></td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button onClick={()=>setAppStatus(a.id, "interview")} className="nh-btn nh-btn-secondary text-xs"><Users size={12}/> Interview</button>
                      <button onClick={()=>setAppStatus(a.id, "hired")} className="nh-btn nh-btn-primary text-xs">Hire</button>
                      <button onClick={()=>setAppStatus(a.id, "rejected")} className="nh-btn nh-btn-ghost text-xs">Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
